import { canBetterDocUsageStillChange } from "@/modules/patients/detail/better-doc/lib/can-better-doc-usage-still-change"
import { isBetterDocRequired } from "@/modules/patients/detail/better-doc/lib/is-better-doc-required"
import type { PointOfCareWithBetterDoc } from "@/modules/patients/detail/better-doc/types"
import {
  isActualGeneralPractitioner,
  isActualHospitalElective,
  isActualSpecialist,
  isMedicalGeneralPractitioner,
  isMedicalHospitalElective,
  isMedicalSpecialist,
} from "@/modules/patients/detail/progress-note/point-of-care/components/point-of-care-content-conditions"
import type { PointOfCareRecord } from "@/modules/patients/detail/progress-note/point-of-care/types"
import type {
  BetterDoc,
  BetterDocConsultationDetail,
  BetterDocRejectionReason,
  BetterDocResultStatus,
} from "@/types/__generated__/union-types"

export const getEffectivePointOfCareWithBetterDoc = (
  medicalPointOfCare: PointOfCareRecord,
  actualPointOfCare: PointOfCareRecord,
): PointOfCareWithBetterDoc | null => {
  if (
    isMedicalGeneralPractitioner(medicalPointOfCare, actualPointOfCare) ||
    isActualGeneralPractitioner(medicalPointOfCare, actualPointOfCare)
  ) {
    return "GENERAL_PRACTITIONER"
  }

  if (
    isMedicalSpecialist(medicalPointOfCare, actualPointOfCare) ||
    isActualSpecialist(medicalPointOfCare, actualPointOfCare)
  ) {
    return "SPECIALIST"
  }

  if (
    isMedicalHospitalElective(medicalPointOfCare, actualPointOfCare) ||
    isActualHospitalElective(medicalPointOfCare, actualPointOfCare)
  ) {
    return "HOSPITAL_ELECTIVE"
  }

  return null
}

export const hasBetterDocUsageBecomeRequired = (
  previousBetterDocUsage: BetterDoc | null,
  currentBetterDocUsage: BetterDoc,
): boolean =>
  previousBetterDocUsage !== null &&
  !isBetterDocRequired(previousBetterDocUsage) &&
  isBetterDocRequired(currentBetterDocUsage)

export const hasCareProviderSelectionBecomeUnblocked = (
  previousMissingConsultationDetails: ReadonlyArray<BetterDocConsultationDetail> | null,
  currentMissingConsultationDetails: ReadonlyArray<BetterDocConsultationDetail>,
): boolean =>
  previousMissingConsultationDetails !== null &&
  canBetterDocUsageStillChange(previousMissingConsultationDetails) &&
  !canBetterDocUsageStillChange(currentMissingConsultationDetails)

type IsCareProviderSelectionBlockedByBetterDocParameters = {
  missingConsultationDetailsThatCanChangeBetterDocUsage: ReadonlyArray<BetterDocConsultationDetail>
  careProviderId: string | null | undefined
  betterDocSuggestionStatus: BetterDocResultStatus | null | undefined
}

export const isCareProviderSelectionBlockedByBetterDoc = ({
  missingConsultationDetailsThatCanChangeBetterDocUsage,
  careProviderId,
  betterDocSuggestionStatus,
}: IsCareProviderSelectionBlockedByBetterDocParameters): boolean =>
  canBetterDocUsageStillChange(missingConsultationDetailsThatCanChangeBetterDocUsage) &&
  !careProviderId &&
  betterDocSuggestionStatus !== "MANUAL_SEARCH_NEEDED"

type IsBetterDocRequestExpectedParameters = {
  betterDocUsage: BetterDoc | null
  careProviderId: string | null | undefined
  betterDocSuggestionStatus: BetterDocResultStatus | null | undefined
  betterDocRejectionReason: BetterDocRejectionReason | null | undefined
}

export const isBetterDocRequestExpected = ({
  betterDocUsage,
  careProviderId,
  betterDocSuggestionStatus,
  betterDocRejectionReason,
}: IsBetterDocRequestExpectedParameters): boolean =>
  isBetterDocRequired(betterDocUsage) &&
  !careProviderId &&
  betterDocSuggestionStatus !== "MANUAL_SEARCH_NEEDED" &&
  !betterDocRejectionReason

type CanRequestBetterDocRecommendationParameters = {
  careProviderId: string | null | undefined
  betterDocSuggestionStatus: BetterDocResultStatus | null | undefined
}

export const canRequestBetterDocRecommendation = ({
  careProviderId,
  betterDocSuggestionStatus,
}: CanRequestBetterDocRecommendationParameters): boolean =>
  betterDocSuggestionStatus !== "MANUAL_SEARCH_NEEDED" && !careProviderId
